import dayjs from "dayjs"
import { useRouter } from "next/router"
import React, { useState } from "react"
import {
  Button,
  Card,
  Checkbox,
  DateInput,
  FormField,
  Heading,
  Link,
  Loader,
  Notice,
  Select,
  Table,
  Text,
  TextField,
} from "@smooth/design-system-components"
import {
  describeApiError,
  formatDateTime,
  formatNumber,
  formatText,
  MessageNotice,
  type ScreenMessage,
} from "src/features/admin/screen-shared"
import { useForFinancePaymentMutations } from "../hooks/useForFinancePaymentMutations"
import { useGetForFinancePayments } from "../hooks/useGetForFinancePayments"
import {
  FOR_FINANCE_PAYMENT_COLUMNS,
  methodLabel,
  salesOffsetLabel,
  statusLabel,
  storeLabel,
  transferApplicationHref,
  type ForFinancePaymentDetail,
  type ForFinancePaymentListQuery,
  type ForFinancePaymentSortKey,
} from "../types"

const SORT_OPTIONS = FOR_FINANCE_PAYMENT_COLUMNS.map(({ key, header }) => ({
  value: key,
  label: header,
}))

const DIRECTION_OPTIONS = [
  { value: "desc", label: "降順" },
  { value: "asc", label: "昇順" },
]

export const paymentHref = (id: number) => `/admin/for-finance-payments/${id}`

// 開いた直後は絞り込まない（送金予定日の欄に今日〜今日を入れておくだけ。SmootherOPE と同じ）
const INITIAL_QUERY: ForFinancePaymentListQuery = {
  search: "",
  dateFrom: "",
  dateTo: "",
  order: "id",
  direction: "desc",
  page: 1,
}

const toRow = (
  p: ForFinancePaymentDetail,
  selectCell: React.ReactNode
): Record<string, React.ReactNode> => ({
  select: selectCell,
  id: <Link href={paymentHref(p.id)}>{p.id}</Link>,
  userId: formatText(p.userId),
  userName: formatText(p.userName),
  storeId: storeLabel(p),
  transferApplicationId: p.transferApplicationId ? (
    <Link href={transferApplicationHref(p.transferApplicationId)} external>
      {p.transferApplicationId}
    </Link>
  ) : (
    "—"
  ),
  customerReferralFeeRateWithTax: formatText(p.customerReferralFeeRateWithTax),
  isSalesOffset: salesOffsetLabel(p.isSalesOffset),
  advancedRemittanceAmount: formatNumber(p.advancedRemittanceAmount),
  reimbursedAt: formatDateTime(p.reimbursedAt),
  applicationId: formatText(p.applicationId),
  status: statusLabel(p.status),
  method: methodLabel(p.method),
  memo: <span className="whitespace-pre-wrap">{formatText(p.memo)}</span>,
  totalInitialCost: formatNumber(p.totalInitialCost),
  advancedPrincipleAmount: formatNumber(p.advancedPrincipleAmount),
  rentCost: formatNumber(p.rentCost),
  customerReferralFeeWithTax: formatNumber(p.customerReferralFeeWithTax),
})

export const ForFinancePaymentList: React.FC = () => {
  const router = useRouter()
  const today = dayjs().format("YYYY-MM-DD")
  const [searchInput, setSearchInput] = useState("")
  const [dateFromInput, setDateFromInput] = useState(today)
  const [dateToInput, setDateToInput] = useState(today)
  const [rowData, setRowData] = useState("")
  const [query, setQuery] = useState<ForFinancePaymentListQuery>(INITIAL_QUERY)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [message, setMessage] = useState<ScreenMessage | null>(null)

  const { payments, totalCount, perPage, isLoading, error, refresh } =
    useGetForFinancePayments(query)
  const { importRow, createTransferApplication, isSubmitting } = useForFinancePaymentMutations()

  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    setQuery((q) => ({
      ...q,
      search: searchInput.trim(),
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      page: 1,
    }))
  }

  const toggleSelected = (id: number, checked: boolean) =>
    setSelectedIds((ids) => (checked ? [...ids, id] : ids.filter((v) => v !== id)))

  const handleCreateTransferApplication = async () => {
    if (selectedIds.length === 0) return
    try {
      const { id } = await createTransferApplication(selectedIds)
      setSelectedIds([])
      setMessage({ tone: "info", text: `立替送金申請を作成しました（ID: ${id}）` })
      await refresh()
    } catch (e) {
      setMessage({ tone: "error", text: describeApiError(e, "立替送金申請を作成できませんでした") })
    }
  }

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!rowData) return
    try {
      const { id } = await importRow(rowData)
      setRowData("")
      setMessage({ tone: "info", text: `取り込みました（ID: ${id}）` })
      await refresh()
    } catch (err) {
      setMessage({ tone: "error", text: describeApiError(err, "取り込めませんでした") })
    }
  }

  const columns = [
    { key: "select", header: "選択" },
    ...FOR_FINANCE_PAYMENT_COLUMNS.map(({ key, header }) => ({ key, header })),
  ]
  const rows = payments.map((p) =>
    toRow(
      p,
      // 立替送金申請に入っていない立替払いにだけ選択欄を出す
      p.transferApplicationId == null ? (
        <Checkbox
          label=""
          aria-label={`ID ${p.id} を選択`}
          checked={selectedIds.includes(p.id)}
          onChange={(e) => toggleSelected(p.id, e.target.checked)}
        />
      ) : null
    )
  )

  return (
    <div className="flex flex-col gap-6">
      <Heading level={1} size="lg">
        For Finance Payments
      </Heading>

      <MessageNotice message={message} />

      <Card variant="outlined">
        <div className="flex flex-wrap items-end gap-6">
          <div className="flex flex-col gap-2">
            <Text size="sm" tone="muted">
              選択中: {selectedIds.length} 件
            </Text>
            <Button
              type="button"
              disabled={selectedIds.length === 0 || isSubmitting}
              onClick={handleCreateTransferApplication}
            >
              立替送金申請の作成
            </Button>
          </div>

          <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
            <FormField label="検索（ステータスの値 または ユーザー名）">
              <TextField
                value={searchInput}
                placeholder="例: unpaid / 山田"
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </FormField>
            <FormField label="送金予定日（開始）">
              <DateInput value={dateFromInput} onChange={(e) => setDateFromInput(e.target.value)} />
            </FormField>
            <FormField label="送金予定日（終了）">
              <DateInput value={dateToInput} onChange={(e) => setDateToInput(e.target.value)} />
            </FormField>
            <Button type="submit" variant="outline">
              検索
            </Button>
          </form>

          <form onSubmit={handleImport} className="flex flex-wrap items-end gap-3">
            <FormField label="行インポート（スプレッドシートの 1 行を貼り付け）">
              <TextField value={rowData} onChange={(e) => setRowData(e.target.value)} />
            </FormField>
            <Button type="submit" variant="outline" disabled={!rowData || isSubmitting}>
              インポート
            </Button>
          </form>
        </div>
      </Card>

      <div className="flex flex-wrap items-end gap-3">
        <FormField label="並び順">
          <Select
            options={SORT_OPTIONS}
            value={query.order}
            onChange={(e) =>
              setQuery((q) => ({
                ...q,
                order: e.target.value as ForFinancePaymentSortKey,
                page: 1,
              }))
            }
          />
        </FormField>
        <FormField label="向き">
          <Select
            options={DIRECTION_OPTIONS}
            value={query.direction}
            onChange={(e) =>
              setQuery((q) => ({ ...q, direction: e.target.value as "asc" | "desc", page: 1 }))
            }
          />
        </FormField>
        <Text size="sm" tone="muted">
          {totalCount.toLocaleString()} 件
        </Text>
      </div>

      {error ? (
        <Notice tone="error" variant="card">
          {describeApiError(error, "一覧を読み込めませんでした")}
        </Notice>
      ) : isLoading ? (
        <Loader />
      ) : payments.length === 0 ? (
        <Notice variant="card">該当する立替払いはありません</Notice>
      ) : (
        <div className="overflow-x-auto">
          <Table
            columns={columns}
            rows={rows}
            onRowClick={(index) => router.push(paymentHref(payments[index].id))}
          />
        </div>
      )}

      <div className="flex items-center justify-center gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={query.page <= 1}
          onClick={() => setQuery((q) => ({ ...q, page: q.page - 1 }))}
        >
          前へ
        </Button>
        <Text size="sm">
          {query.page} / {totalPages} ページ
        </Text>
        <Button
          type="button"
          variant="outline"
          disabled={query.page >= totalPages}
          onClick={() => setQuery((q) => ({ ...q, page: q.page + 1 }))}
        >
          次へ
        </Button>
      </div>
    </div>
  )
}
